import Dropdown from "components/blocks/Dropdown";
import FormInput from "components/blocks/FormInput";
import LoadingOverlay from "components/blocks/LoadingOverlay";
import { useEffect, useState } from "react";
import Modal from "components/blocks/Modal";
import { useTranslation } from "react-i18next";
import { useNotification } from "hooks/useNotification";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import FileUploadWithPreview, { SelectedFilesType } from "components/blocks/FileUploadWithPreview";
import {RewardsService} from "../../services/RewardsService";
import {RewardResponse, UpdateRewardRequest} from "../../services/RewardsService/models";
import LookupDropdown from "../blocks/LookupDropdown";

type RewardConfirmationModalProps = {
  isOpen: boolean;
  onClose: (refetch: boolean) => void;
  type: "examiner" | "author" | "referee";
  data?: RewardResponse | undefined
};


const updateRewardSchema = z.object({
  rewardStatusId:z.number({ message: "Common:requiredField" }).int().min(1, "Common:requiredField"),
  value: z
    .number()
    .min(0, { message: "Common:fieldRequired" })
    .max(999999, { message: "pleaseCheckRewardAmount" }),
  fileId: z.string().uuid({ message: "Common:invalidFileId" }).nullish(),
});

const RewardConfirmationModal = ({ isOpen, onClose, data, type }: RewardConfirmationModalProps) => {
  const { t } = useTranslation();
  const [confirmationModalLoading, setConfirmationModalLoading] = useState(false);
  const [files, setFiles] = useState<SelectedFilesType[]>([]);
  const { showNotification } = useNotification();

  useEffect(() => {
    if (!isOpen || confirmationModalLoading || !data?.id) {
      return;
    }

    const fetchReward = async () => {
      try {
        setConfirmationModalLoading(true);
        let response = null;
        if(type === "examiner") {
          response = await RewardsService.getExaminersRewardById(data.id);
        }
        else if (type === "referee") {
          response = await RewardsService.getRefereesRewardById(data.id);
        }
        else if (type === "author") {
          response = await RewardsService.getAuthorsRewardById(data.id);
        }
        

        if (response&& !response.hasError) {
          if (response.data.file) {
            setValue("fileId", response.data.file.id);
            setFiles([
              {
                file: new File([""], response.data.file.name || `attachment.${response.data.file.extension}`, {
                  type: response.data.file.type || "",
                }),
                fileName: response.data.file.name || `attachment.${response.data.file.extension}`,
                uuid: response.data.file.id,
                size: response.data.file.size,
              },
            ]);
          }
        }
      } catch (error) {
        showNotification({
          type: "error",
          description: t("Common:internalServerError"),
        });
      } finally {
        setConfirmationModalLoading(false);
      }
    };

    fetchReward();
  }, [data?.id]);

  const {
    reset,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    setError,
  } = useForm<z.infer<typeof updateRewardSchema>>({
    reValidateMode: "onBlur",
    resolver: zodResolver(updateRewardSchema),
    values: {
      rewardStatusId: data?.rewardStatusId ??0,
      value: data?.value ?? 0,
      fileId: null,
    },
  });

  const onModalClose = (refetch: boolean) => {
    reset();
    setFiles([]);
    onClose(refetch);
  };

  const onConfirm = async (formValues: z.infer<typeof updateRewardSchema>) => {
    try {
      setConfirmationModalLoading(true);
      let response = null;
      if(type === "examiner") {
          response = await RewardsService.updateExaminerReward(data?.id ??0, formValues as UpdateRewardRequest);
      }
      else if (type === "referee") {
        response = await RewardsService.updateRefereeReward(data?.id??0, formValues as UpdateRewardRequest);
      }
      else if (type === "author") {
        response = await RewardsService.updateAuthorReward(data?.id ?? 0, formValues as UpdateRewardRequest);
      }

    

      if (response &&!response.hasError) {
        onModalClose(true);
        showNotification({
          type: "success",
          description: t("Common:rewardUpdatedSuccessfully"),
        });
      } else {
        showNotification({
          type: "error",
          description: t(response?.description ??""),
        });
      }
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setConfirmationModalLoading(false);
    }
  };

  const onReject = async () => {
    try {
      setConfirmationModalLoading(true);
      let response = null;
      if(type === "examiner") {
        response = await RewardsService.rejectExaminerReward(data?.id ??0);
      }
      else if (type === "referee") {
        response = await RewardsService.rejectRefereeReward(data?.id ?? 0);
      } 
      else if (type === "author") {
        response = await RewardsService.rejectAuthorReward(data?.id ?? 0);
      }
   

      if (response && !response.hasError) {
        onModalClose(true);
        showNotification({
          type: "success",
          description: t("Common:rewardRejectedSuccessfully"),
        });
      } else {
        showNotification({
          type: "error",
          description: t(response?.description??""),
        });
      }
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setConfirmationModalLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onModalClose(false)}
      title={ t("Common:rewardEdit")}
      size="lg"
      containerClassName="bg-white"
      bodyMargin="my-1"
      titleClassName="fs-6"
    >
      <form onSubmit={handleSubmit(onConfirm)}>
        <div className="d-flex flex-column gap-3 position-relative">
          <LoadingOverlay visible={confirmationModalLoading} />
          <div className="divider" />

          <div className="d-flex gap-3">
            <FormInput
              wrapperClassName="flex-1"
              label={t("Common:rewardAmount")}
              leftElement={<span className="text-primary">{t("Common:riyal")}</span>}
              value={watch("value")}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || isNaN(+value)) {
                  setValue("value", 0);
                } else {
                  setValue("value", +value);
                  setError("value", { message: +value > 999999 ? "Common:pleaseCheckRewardAmount" : "" });
                }
              }}
              error={t(errors.value?.message || "")}
            />
            <LookupDropdown service={"lookupService"} 
                            endpoint={"GetAllRewardStatusDropDown"}
                            textValueKey="details.0.displayName"
                            idValueKey="id"
                            useReactSelect
                            error={errors.rewardStatusId?.message || ""}
                            label={t("Common:status")}
                            value={watch("rewardStatusId")}
                            onChange={(value, item) => {
                                if (value) {
                                    setValue("rewardStatusId", item.id as number);
                                    setError("rewardStatusId", { message: "" });
                                } else {
                                    setError("rewardStatusId", { message: "Common:fieldRequired" });
                                }
                            }}
            />
            
          </div>
          <FileUploadWithPreview
            // maxNumberOfFiles={1}
            isMultiple={false}
            formProps={{
              clickHereForLabel: t("Common:toAddBillOfExchange"),
            }}
            files={files}
            setFiles={(files: SelectedFilesType[]) => {
              if (files && files.length > 0) {
                setValue("fileId", files[0].uuid);
              } else {
                setValue("fileId", null);
              }

              setFiles(files);
              setError("fileId", { message: "" });
            }}
          />
          {errors.fileId && <span className="text-danger text-center">{t(errors.fileId?.message || "")}</span>}

          <div className="divider" />

          <div className="d-flex gap-2 justify-content-center mt-2">
            <button className="p-10-56P btn btn-primary" type="submit">
              {t("Common:save")}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default RewardConfirmationModal;
